import config from '@/config';
import { authorizationHeaders } from '@/stores/useAuthStore';

/** Durée de vie de l'adresse d'un fichier ouvert dans un onglet : le temps qu'il le charge */
const OPENED_URL_LIFETIME = 60_000;

/**
 * Fichier servi par l'API (ex. le PDF d'une feuille, GET /time_sheets/{id}/pdf) et le nom qu'elle lui donne
 * (Content-Disposition). model-core ne lit que du JSON : même authentification (authorizationHeaders) et même erreur
 * levée que lui (le « detail » d'API Platform, lu par getApiErrorMessage).
 *
 * @param path chemin de la ressource, sans la base de l'API
 */
async function fetchFile(path: string): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await fetch(`${config.apiUrl}${path}`, { headers: await authorizationHeaders() });
  if (!response.ok) {
    const json = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    throw (json && (json.detail ?? json['hydra:description'] ?? json.message)) ?? response.statusText;
  }

  return { blob: await response.blob(), fileName: fileNameOf(response.headers.get('Content-Disposition')) };
}

/**
 * Télécharge un fichier de l'API sous le nom qu'elle donne
 *
 * @param fallbackName nom du fichier si l'API n'en donne pas
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const { blob, fileName } = await fetchFile(path);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = fileName ?? fallbackName;
  link.href = url;
  link.click();
  // Après le clic : le navigateur a lancé le téléchargement
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Ouvre un fichier de l'API dans un nouvel onglet (le PDF s'affiche dans la visionneuse du navigateur). L'onglet est
 * ouvert au clic, avant la requête : ouvert après, il serait bloqué comme une fenêtre surgissante. Il est refermé si
 * l'API refuse.
 */
export async function openFile(path: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const { blob } = await fetchFile(path);
    const url = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = url;
    } else {
      // Onglet bloqué malgré tout : le fichier s'ouvre à la place de l'appli
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), OPENED_URL_LIFETIME);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** Nom de fichier d'un Content-Disposition : filename* (UTF-8, accents compris) avant filename (ASCII de repli) */
export function fileNameOf(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const encoded = /filename\*=(?:utf-8|UTF-8)''([^;]+)/.exec(contentDisposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Encodage invalide : le nom de repli
    }
  }
  const plain = /filename="?([^";]+)"?/.exec(contentDisposition);

  return plain ? plain[1].trim() : null;
}
